/**
 * Distributors API Client
 * Handles all API operations for the Material Ordering System
 */

import { apiClient, fetchApiBlob } from './api';
import { loadCatalogImage } from './catalogImageLoader';
import { authService } from './auth';
import { submissionOperation, clearSubmissionOperation } from './distributorSubmission';

// Types
export type IntegrationType = 'email' | 'api_abc' | 'api_srs' | 'api_qxo';

export type Distributor = {
  id: number;
  name: string;
  sales_rep_name: string;
  phone_number: string;
  locations: Array<{
    address: string;
    city: string;
    state: string;
    zip: string;
  }>;
  default_order_email: string;
  notes?: string;
  is_active: boolean;
  integration_type?: IntegrationType;
  created_at: string;
  updated_at: string;
};

export type PriceListItem = {
  id: number;
  distributor: number;
  product_id?: string;
  product_name: string;
  unit_of_measure: string;
  unit_price: string;
  category: string;
  is_active: boolean;
  last_updated: string;
};

export type OrderItem = {
  id?: number;
  price_list_item?: number;
  product_name: string;
  product_id?: string;
  unit_price: string;
  unit_of_measure: string;
  quantity: number;
  total_price?: string;
  notes?: string;
  // API-order line fields (distributor API integrations)
  external_line_id?: string;
  is_dimensional?: boolean;
  length_value?: string | number | null;
  length_uom?: string;
  distributor_uom?: string;
};

// Vendor-specific vocabularies — ABC uses COM/CPU/..., SRS GROUND_DROP/...,
// QXO GDANY/... The authoritative list per distributor comes from
// GET /distributors/{id}/integration/, so these are plain strings.
export type DeliveryServiceCode = string;
export type AppointmentTypeCode = string;

export type IntegrationCapabilities = {
  live_pricing: boolean;
  api_ordering: boolean;
  order_status: boolean;
  catalog_search: boolean;
  webhooks: boolean;
};

export type IntegrationLimits = {
  pricing_max_lines: number;
  order_max_lines: number;
  po_number_max_length: number;
  item_number_max_length: number;
  search_min_query_length: number;
};

export type ConnectionFormField = {
  name: string;
  label: string;
  type: 'text' | 'password' | 'select' | 'number' | 'hidden';
  required?: boolean;
  help_text?: string;
  options?: Array<{ value: string; label: string }>;
};

/** Everything the UI needs to render a distributor without naming a vendor. */
export type DistributorIntegration = {
  integration_type: IntegrationType;
  slug: string;
  display_name: string;
  account_noun?: string;
  connect_mode: 'oauth_redirect' | 'credentials' | null;
  credentials_configured: boolean;
  mock_mode: boolean;
  orders_simulated?: boolean;
  catalog_source?: 'sandbox' | 'sample' | 'api';
  api_environment?: string | null;
  can_submit_orders?: boolean;
  capabilities: IntegrationCapabilities;
  limits?: IntegrationLimits;
  delivery_services?: Array<{ code: string; label: string; is_pickup: boolean }>;
  default_delivery_service?: string;
  appointment_types?: Array<{
    code: string; label: string; requires_from: boolean; requires_to: boolean;
  }>;
  supports_delivery_appointment?: boolean;
  supports_product_images?: boolean;
  supports_dimensional_items?: boolean;
  required_order_fields?: string[];
  connection_form: ConnectionFormField[];
};

export type DeliveryAppointment = {
  type_code: AppointmentTypeCode;
  from_time?: string; // "HH:MM"
  to_time?: string;
  time_zone_code?: string;
  instructions?: string;
};

export type OrderEvent = {
  id: number;
  source: 'webhook' | 'poll' | 'user' | 'system';
  event_type: string;
  old_status: string;
  new_status: string;
  external_status: string;
  occurred_at: string;
};

export type Order = {
  can_edit?: boolean;
  takeoff_source?: { review_id: number; measurement: number | null; detailed_measurement: number; source_revision: number; proposal: number | null; proposal_number: string; opportunity: number | null; opportunity_label: string; live_catalog: boolean } | null;
  id: number;
  order_number: string;
  distributor: number;
  distributor_name?: string;  // From list view
  distributor_details?: Distributor;  // From detail view
  status: 'draft' | 'submitted' | 'processing' | 'completed' | 'cancelled';
  total_amount: string;
  notes?: string;
  items?: OrderItem[];
  delivery_address?: any;
  requested_delivery_date?: string;
  created_at: string;
  submitted_at?: string;
  created_by_name?: string;
  // Job linkage + API submission fields
  job?: number | null;
  job_number?: string | null;
  submission_method?: 'email' | 'api';
  po_number?: string;
  ship_to_number?: string;
  branch_number?: string;
  branch_name?: string;
  delivery_service?: DeliveryServiceCode | '';
  delivery_appointment?: DeliveryAppointment | Record<string, never> | null;
  currency?: string;
  confirmation_number?: string;
  external_order_number?: string;
  external_status?: string;
  last_status_sync_at?: string | null;
  external_request_id?: string;
  /** Provider-specific extras (SRS queue id + delivery tracking URL, QXO job). */
  provider_metadata?: Record<string, unknown> | null;
  api_error?: string;
  api_submission_state?: '' | 'pending' | 'unknown';
  api_submission_started_at?: string | null;
  subtotal?: string | null;
  tax_amount?: string | null;
  events?: OrderEvent[];
};

export type PaginatedResponse<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

// ---- Material Catalog Types ----

// Maps to MaterialUnitType.choices on the backend
export type MaterialUnitType = 'area' | 'linear' | 'volume' | 'weight' | 'count';

export type MaterialCategory = {
  id: number;
  name: string;
  description?: string;
  unit_type: MaterialUnitType;
  display_order: number;
  is_active: boolean;
  specifications_count?: number;
  created_at: string;
  updated_at: string;
};

export type MaterialSpecification = {
  id: number;
  category: number;
  category_name?: string;
  name: string;
  description?: string;
  standard_unit: string;
  coverage_per_unit: string;
  waste_factor_percent: string;
  calculation_material_type?: string;
  default_unit_cost?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type StandardMaterial = {
  id: number;
  name: string;
  material_spec?: number | null;
  material_spec_name?: string | null;
  calculation_key: string;
  default_quantity_formula?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type ProductMapping = {
  id: number;
  distributor: number;
  distributor_name?: string;
  material_spec: number;
  material_spec_name?: string;
  price_list_item: number;
  product_name?: string;
  product_id?: string;
  unit_price?: string;
  is_primary_match: boolean;
  mapping_confidence: string;
  conversion_factor: string;
  notes?: string;
  created_by?: number | null;
  verified_at?: string | null;
  created_at: string;
  updated_at: string;
};

// Shape returned by product-mappings/suggest_distributor/
export type DistributorSuggestionResult = {
  distributor_id: number;
  distributor_name: string;
  coverage_percent: number;
  materials_available: number;
  materials_requested: number;
  average_price: number | null;
  score: number;
};

export type DistributorSuggestionResponse = {
  recommended: DistributorSuggestionResult | null;
  alternatives: DistributorSuggestionResult[];
  all_distributors: DistributorSuggestionResult[];
};

// One entry returned by product-mappings/map_materials/ (shape varies by match outcome)
export type MappedMaterialResult = {
  price_list_item_id?: number;
  product_name?: string;
  product_id?: string;
  unit_price?: number;
  quantity?: number;
  original_quantity?: number;
  material_spec_id?: number;
  material_spec_name?: string;
  mapping_confidence?: number;
  confidence_level?: string;
  notes?: string;
  requires_manual_selection?: boolean;
  alternatives?: Array<{
    price_list_item_id: number;
    product_name: string;
    unit_price: number;
    mapping_confidence: number;
  }>;
  suggestions?: Array<{
    price_list_item_id: number;
    product_name: string;
    product_id?: string;
    unit_price: number;
    similarity_score: number;
  }>;
  message?: string;
  error?: string;
};

// Helper function to build query strings
const buildQueryString = (params: any) => {
  if (!params) return '';
  const searchParams = new URLSearchParams();
  Object.keys(params).forEach(key => {
    if (params[key] !== undefined && params[key] !== null) {
      searchParams.append(key, params[key]);
    }
  });
  const query = searchParams.toString();
  return query ? `?${query}` : '';
};

// Custom fetch for paginated responses that preserves structure
const fetchPaginated = async <T,>(endpoint: string): Promise<PaginatedResponse<T>> => {
  const accessToken = authService.getAccessToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
  };
  
  let response = await fetch(`${window.location.origin}${endpoint}`, {
    method: 'GET',
    headers,
  });
  
  if (!response.ok) {
    if (response.status === 401) {
      // Try to refresh token
      try {
        const newToken = await authService.refreshAccessToken();
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(`${window.location.origin}${endpoint}`, {
          method: 'GET',
          headers,
        });
        if (response.ok) {
          return await response.json();
        }
      } catch {
        authService.clearAccessToken();
        window.location.href = `/login/?next=${encodeURIComponent(window.location.pathname)}`;
      }
    }
    const responseData = await response.json().catch(() => ({}));
    throw Object.assign(new Error(responseData.error || responseData.detail || `HTTP error! status: ${response.status}`), {
      status: response.status,
      responseData,
    });
  }
  
  const data = await response.json();
  
  // Return the full paginated structure
  return data as PaginatedResponse<T>;
};

// Distributor API
export const distributorsApi = {
  // List distributors with optional filters
  list: async (params?: { is_active?: boolean; search?: string; page?: number }) => {
    const queryString = buildQueryString(params);
    
    // Use custom fetch to preserve paginated structure
    const response = await fetchPaginated<Distributor>(`/api/distributors/distributors${queryString}`);
    
    return response;
  },

  // Get single distributor
  get: async (id: number) => {
    const response = await apiClient.get(`/api/distributors/distributors/${id}/`);
    return response.data as Distributor;
  },

  // Create distributor (admin only)
  create: async (data: Partial<Distributor>) => {
    const response = await apiClient.post('/api/distributors/distributors/', data);
    return response.data as Distributor;
  },

  // Update distributor (admin only)
  update: async (id: number, data: Partial<Distributor>) => {
    const response = await apiClient.patch(`/api/distributors/distributors/${id}/`, data);
    return response.data as Distributor;
  },

  // Delete distributor (admin only)
  delete: async (id: number) => {
    await apiClient.delete(`/api/distributors/distributors/${id}/`);
  },

  // Activate/deactivate
  activate: async (id: number) => {
    const response = await apiClient.post(`/api/distributors/distributors/${id}/activate/`, {});
    return response.data;
  },

  deactivate: async (id: number) => {
    const response = await apiClient.post(`/api/distributors/distributors/${id}/deactivate/`, {});
    return response.data;
  },
};

// Price List API
export const priceListApi = {
  // List price list items
  list: async (params?: { 
    distributor?: number; 
    category?: string; 
    search?: string;
    is_active?: boolean;
    page_size?: number;
  }) => {
    const queryString = buildQueryString(params);
    
    // Use custom fetch to preserve paginated structure
    const response = await fetchPaginated<PriceListItem>(`/api/distributors/price-list-items${queryString}`);
    
    return response;
  },

  // Get single item
  get: async (id: number) => {
    const response = await apiClient.get(`/api/distributors/price-list-items/${id}/`);
    return response.data as PriceListItem;
  },

  // Create item (admin only)
  create: async (data: Partial<PriceListItem>) => {
    const response = await apiClient.post('/api/distributors/price-list-items/', data);
    return response.data as PriceListItem;
  },

  // Update item (admin only)
  update: async (id: number, data: Partial<PriceListItem>) => {
    const response = await apiClient.patch(`/api/distributors/price-list-items/${id}/`, data);
    return response.data as PriceListItem;
  },

  // Delete item (admin only)
  delete: async (id: number) => {
    await apiClient.delete(`/api/distributors/price-list-items/${id}/`);
  },

  // Bulk import
  bulkImport: async (distributor_id: number, items: any[], replace_existing = false) => {
    const response = await apiClient.post('/api/distributors/price-list-items/bulk_import/', {
      distributor_id,
      items,
      replace_existing
    });
    return response.data;
  },

  // Upload file for parsing
  uploadFile: async (file: File, distributor_id: number, replace_existing = false) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('distributor_id', distributor_id.toString());
    formData.append('file_type', file.name.endsWith('.pdf') ? 'pdf' : 'excel');
    formData.append('replace_existing', replace_existing.toString());

    const response = await apiClient.post('/api/distributors/price-list-items/upload_file/', formData);
    return response.data;
  },
};

// Payload accepted by material-orders create/update (draft fields)
export type OrderDraftPayload = {
  contact_phone?: string;
  contact_name?: string;
  contact_email?: string;
  distributor: number;
  items: OrderItem[];
  notes?: string;
  delivery_address?: any;
  requested_delivery_date?: string | null;
  job?: number | null;
  po_number?: string;
  ship_to_number?: string;
  branch_number?: string;
  branch_name?: string;
  delivery_service?: DeliveryServiceCode | '';
  delivery_appointment?: DeliveryAppointment | Record<string, never> | null;
};

// Body for submitting an API-integrated order (all optional — the backend
// falls back to values persisted on the draft)
export type ApiSubmitPayload = {
  contact_phone?: string;
  contact_name?: string;
  contact_email?: string;
  po_number?: string;
  ship_to_number?: string;
  branch_number?: string;
  branch_name?: string;
  delivery_service?: DeliveryServiceCode;
  requested_delivery_date?: string | null;
  delivery_appointment?: DeliveryAppointment | null;
};

// Orders API
export const ordersApi = {
  // List orders
  list: async (params?: {
    status?: string;
    distributor?: number;
    job?: number;
    submission_method?: 'email' | 'api';
    ordering?: string;
    page?: number;
  }) => {
    const queryString = buildQueryString(params);

    // Use custom fetch to preserve paginated structure
    const response = await fetchPaginated<Order>(`/api/distributors/material-orders${queryString}`);

    return response;
  },

  // Get single order
  get: async (id: number) => {
    const response = await apiClient.get(`/api/distributors/material-orders/${id}/`);
    return response.data as Order;
  },

  // Create order
  create: async (data: OrderDraftPayload) => {
    const response = await apiClient.post('/api/distributors/material-orders/', data);
    return response.data as Order;
  },

  // Update order
  update: async (id: number, data: Partial<Order>) => {
    const response = await apiClient.patch(`/api/distributors/material-orders/${id}/`, data);
    return response.data as Order;
  },

  // Submit order. Email distributors take {generate_pdf, send_email};
  // API-integrated distributors take ApiSubmitPayload. The response is the
  // order detail (API path) or detail + email_result (email path); failures
  // surface as HTTP errors whose body carries {error, api_error, code}.
  submit: async (
    id: number,
    options?:
      | { generate_pdf?: boolean; send_email?: boolean; recipient_email?: string }
      | ApiSubmitPayload,
  ) => {
    try {
      const response = await apiClient.post(`/api/distributors/material-orders/${id}/submit/`, options || {}, {
        'X-Operation-ID': submissionOperation(id),
      });
      clearSubmissionOperation(id);
      return response.data as Order & { email_result?: { success: boolean; message: string } };
    } catch (error: any) {
      const status = error.status ?? error.response?.status;
      const data = error.responseData ?? error.response?.data;
      if (status >= 400 && status < 500 && data?.outcome !== 'unknown' && status !== 409) {
        clearSubmissionOperation(id);
      }
      throw error;
    }
  },

  resolveSubmission: async (id: number, data: {
    resolution: 'accepted' | 'not_placed'; reference: string; confirmation_number?: string;
  }) => {
    const response = await apiClient.post(`/api/distributors/material-orders/${id}/resolve-submission/`, data);
    clearSubmissionOperation(id);
    return response.data as Order;
  },

  // Pull the latest distributor status for an API-submitted order
  sync: async (id: number) => {
    const response = await apiClient.post(`/api/distributors/material-orders/${id}/sync-status/`, {});
    return response.data as Order;
  },

  // Cancel order
  cancel: async (id: number) => {
    const response = await apiClient.post(`/api/distributors/material-orders/${id}/cancel/`, {});
    return response.data as Order;
  },

  // Duplicate order
  duplicate: async (id: number) => {
    const response = await apiClient.post(`/api/distributors/material-orders/${id}/duplicate/`, {});
    return response.data as Order;
  },

  // Download PDF
  downloadPdf: async (id: number) => {
    const accessToken = authService.getAccessToken();
    const headers: HeadersInit = {
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    };
    
    const response = await fetch(`${window.location.origin}/api/distributors/material-orders/${id}/download_pdf/`, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      // Try to refresh token if unauthorized
      if (response.status === 401) {
        try {
          const newToken = await authService.refreshAccessToken();
          headers['Authorization'] = `Bearer ${newToken}`;
          const retryResponse = await fetch(`${window.location.origin}/api/distributors/material-orders/${id}/download_pdf/`, {
            method: 'GET',
            headers,
          });
          if (retryResponse.ok) {
            return await retryResponse.blob();
          }
        } catch {
          authService.clearAccessToken();
          window.location.href = `/login/?next=${encodeURIComponent(window.location.pathname)}`;
        }
      }
      throw new Error(`Failed to download PDF: ${response.statusText}`);
    }
    
    // Return the response as a blob
    return await response.blob();
  },
};

// Material Categories API
export const materialCategoriesApi = {
  // List material categories
  list: async (params?: {
    is_active?: boolean;
    unit_type?: string;
    search?: string;
    ordering?: string;
    page?: number;
  }) => {
    const queryString = buildQueryString(params);
    return fetchPaginated<MaterialCategory>(`/api/distributors/material-categories${queryString}`);
  },

  // Get single category
  get: async (id: number) => {
    const response = await apiClient.get(`/api/distributors/material-categories/${id}/`);
    return response.data as MaterialCategory;
  },

  // Create category
  create: async (data: Partial<MaterialCategory>) => {
    const response = await apiClient.post('/api/distributors/material-categories/', data);
    return response.data as MaterialCategory;
  },

  // Update category
  update: async (id: number, data: Partial<MaterialCategory>) => {
    const response = await apiClient.patch(`/api/distributors/material-categories/${id}/`, data);
    return response.data as MaterialCategory;
  },

  // Delete category
  delete: async (id: number) => {
    await apiClient.delete(`/api/distributors/material-categories/${id}/`);
  },
};

// Material Specifications API (material-specifications/)
export const materialSpecsApi = {
  // List material specifications
  list: async (params?: {
    category?: number;
    is_active?: boolean;
    standard_unit?: string;
    search?: string;
    ordering?: string;
    page?: number;
  }) => {
    const queryString = buildQueryString(params);
    return fetchPaginated<MaterialSpecification>(`/api/distributors/material-specifications${queryString}`);
  },

  // Get single specification
  get: async (id: number) => {
    const response = await apiClient.get(`/api/distributors/material-specifications/${id}/`);
    return response.data as MaterialSpecification;
  },

  // Create specification
  create: async (data: Partial<MaterialSpecification>) => {
    const response = await apiClient.post('/api/distributors/material-specifications/', data);
    return response.data as MaterialSpecification;
  },

  // Update specification
  update: async (id: number, data: Partial<MaterialSpecification>) => {
    const response = await apiClient.patch(`/api/distributors/material-specifications/${id}/`, data);
    return response.data as MaterialSpecification;
  },

  // Delete specification
  delete: async (id: number) => {
    await apiClient.delete(`/api/distributors/material-specifications/${id}/`);
  },
};

// Standard Materials API (standard-materials/)
export const standardMaterialsApi = {
  // List standard materials
  list: async (params?: {
    is_active?: boolean;
    material_spec?: number;
    search?: string;
    ordering?: string;
    page?: number;
  }) => {
    const queryString = buildQueryString(params);
    return fetchPaginated<StandardMaterial>(`/api/distributors/standard-materials${queryString}`);
  },

  // Get single standard material
  get: async (id: number) => {
    const response = await apiClient.get(`/api/distributors/standard-materials/${id}/`);
    return response.data as StandardMaterial;
  },

  // Create standard material
  create: async (data: Partial<StandardMaterial>) => {
    const response = await apiClient.post('/api/distributors/standard-materials/', data);
    return response.data as StandardMaterial;
  },

  // Update standard material
  update: async (id: number, data: Partial<StandardMaterial>) => {
    const response = await apiClient.patch(`/api/distributors/standard-materials/${id}/`, data);
    return response.data as StandardMaterial;
  },

  // Delete standard material
  delete: async (id: number) => {
    await apiClient.delete(`/api/distributors/standard-materials/${id}/`);
  },
};

// Product Mappings API (product-mappings/) incl. map_materials/suggest_distributor/bulk_create
export const productMappingsApi = {
  // List product mappings
  list: async (params?: {
    distributor?: number;
    material_spec?: number;
    is_primary_match?: boolean;
    search?: string;
    ordering?: string;
    page?: number;
  }) => {
    const queryString = buildQueryString(params);
    return fetchPaginated<ProductMapping>(`/api/distributors/product-mappings${queryString}`);
  },

  // Get single mapping
  get: async (id: number) => {
    const response = await apiClient.get(`/api/distributors/product-mappings/${id}/`);
    return response.data as ProductMapping;
  },

  // Create mapping
  create: async (data: Partial<ProductMapping>) => {
    const response = await apiClient.post('/api/distributors/product-mappings/', data);
    return response.data as ProductMapping;
  },

  // Update mapping
  update: async (id: number, data: Partial<ProductMapping>) => {
    const response = await apiClient.patch(`/api/distributors/product-mappings/${id}/`, data);
    return response.data as ProductMapping;
  },

  // Delete mapping
  delete: async (id: number) => {
    await apiClient.delete(`/api/distributors/product-mappings/${id}/`);
  },

  // Suggest the best distributor for a set of material specs
  suggestDistributor: async (material_spec_ids: number[]) => {
    const response = await apiClient.post('/api/distributors/product-mappings/suggest_distributor/', {
      material_spec_ids,
    });
    return response.data as DistributorSuggestionResponse;
  },

  // Map material line items to a distributor's products
  mapMaterials: async (
    material_line_items: Array<{
      material_spec_id: number;
      quantity?: number;
      calculation_key?: string;
    }>,
    distributor_id: number,
    include_suggestions = true,
  ) => {
    const response = await apiClient.post('/api/distributors/product-mappings/map_materials/', {
      material_line_items,
      distributor_id,
      include_suggestions,
    });
    return response.data as MappedMaterialResult[];
  },

  // Bulk create mappings
  bulkCreate: async (
    mappings: Array<{
      distributor_id: number;
      material_spec_id: number;
      price_list_item_id: number;
      is_primary?: boolean;
      confidence?: number;
      conversion_factor?: number;
      notes?: string;
    }>,
  ) => {
    const response = await apiClient.post('/api/distributors/product-mappings/bulk_create/', {
      mappings,
    });
    return response.data as { created: number; mappings: ProductMapping[] };
  },
};

// ---- Distributor API integration (ABC Supply) ----

export type ConnectionStatus = 'pending' | 'active' | 'error' | 'disconnected';

export type ShipToBranch = {
  number: string;
  name: string;
  homeBranch: boolean;
  status?: string;
  delivery_services?: string[];
  supports_pickup?: boolean;
  pickup_times_weekday?: string[];
  pickup_times_saturday?: string[];
};

export type ShipTo = {
  id: number;
  number: string;
  name: string;
  address: Record<string, string>;
  branches: ShipToBranch[];
  payment_terms: Record<string, string>;
  is_default: boolean;
  is_active: boolean;
  last_synced_at: string | null;
};

export type DistributorConnection = {
  id?: number;
  distributor?: number;
  status: ConnectionStatus;
  connected_by?: number | null;
  connected_by_name?: string;
  scopes?: string;
  external_account_info?: Record<string, unknown> | null;
  default_branch_number?: string;
  last_refreshed_at?: string | null;
  error_message?: string;
  ship_tos?: ShipTo[];
  created_at?: string;
};

export type CatalogUom = {
  code: string;
  name: string;
  description: string;
  is_stocking: boolean;
};

export type CatalogVariation = {
  value: number;
  uom: string;
  size: string;
};

export type CatalogProduct = {
  item_number: string;
  description: string;
  family_name: string;
  supplier_name: string;
  uoms: CatalogUom[];
  is_dimensional: boolean;
  dimension_options: CatalogVariation[];
  image_asset_id: string;
  category_path: string;
  status: string;
};

export type QuoteLineRequest = {
  id: string; // client-generated cart line id — echoed back per line
  item_number: string;
  quantity: number;
  uom?: string;
  is_dimensional?: boolean;
  length_value?: number | null;
  length_uom?: string;
};

export type QuoteLineResult = {
  id: string;
  item_number: string;
  quantity: number;
  uom: string;
  unit_price: string | null; // "0.00" is a VALID price ("confirm with branch")
  currency: string;
  status_code: string;
  status_message: string;
  ok: boolean;
};

export type QuoteResponse = {
  ship_to_number: string;
  branch_number: string;
  purpose: string;
  lines: QuoteLineResult[];
};

// Connection lifecycle (org-level distributor account)
export const integrationApi = {
  /** Capabilities, limits and connect mode for one distributor. */
  get: async (distributorId: number) => {
    const response = await apiClient.get(
      `/api/distributors/distributors/${distributorId}/integration/`,
    );
    return response.data as DistributorIntegration;
  },
};

export const connectionApi = {
  exportData: async (distributorId: number) => {
    return fetchApiBlob(`/api/distributors/distributors/${distributorId}/export-data/`);
  },
  // Start the OAuth flow — backend returns the vendor authorize URL
  startOAuth: async (distributorId: number, consent?: { accepted: boolean; terms_version: string; disclosure_version: string }) => {
    const response = await apiClient.post(`/api/distributors/distributors/${distributorId}/connect/`, consent || {});
    return response.data as { auth_url: string };
  },

  get: async (distributorId: number) => {
    const response = await apiClient.get(`/api/distributors/distributors/${distributorId}/connection/`);
    return response.data as DistributorConnection;
  },

  setDefaults: async (
    distributorId: number,
    data: { ship_to_number?: string; branch_number?: string },
  ) => {
    const response = await apiClient.post(
      `/api/distributors/distributors/${distributorId}/connection/set-defaults/`,
      data,
    );
    return response.data as DistributorConnection;
  },

  /**
   * Connect a vendor account from org-supplied credentials (QXO password
   * grant, SRS customer code). The payload shape is driven by the provider's
   * connection_form; secrets are posted once and never stored client-side.
   */
  connectWithCredentials: async (distributorId: number, payload: Record<string, unknown>) => {
    const response = await apiClient.post(
      `/api/distributors/distributors/${distributorId}/connection/credentials/`,
      payload,
    );
    return response.data as DistributorConnection;
  },

  disconnect: async (distributorId: number) => {
    const response = await apiClient.post(`/api/distributors/distributors/${distributorId}/disconnect/`, {});
    return response.data as { status: ConnectionStatus };
  },

  // Re-sync Ship-To accounts (doubles as the connection "Test")
  refreshAccounts: async (distributorId: number) => {
    const response = await apiClient.post(
      `/api/distributors/distributors/${distributorId}/refresh-accounts/`,
      {},
    );
    return response.data as DistributorConnection;
  },
};

// Live catalog proxy (works pre-connection — platform token server-side)
export const catalogApi = {
  search: async (
    distributorId: number,
    params: { q: string; page?: number; page_size?: number; branch?: string },
  ) => {
    const queryString = buildQueryString(params);
    return fetchPaginated<CatalogProduct>(
      `/api/distributors/distributors/${distributorId}/catalog/search/${queryString}`,
    );
  },

  getItem: async (distributorId: number, itemNumber: string) => {
    const response = await apiClient.get(
      `/api/distributors/distributors/${distributorId}/catalog/items/${encodeURIComponent(itemNumber)}/`,
    );
    return response.data as CatalogProduct;
  },

  // Share a paced queue across thumbnails; never start a page-sized burst.
  getItemImage: (distributorId: number, assetId: string, signal?: AbortSignal): Promise<Blob> =>
    loadCatalogImage(
      `/api/distributors/distributors/${distributorId}/catalog/images/${encodeURIComponent(assetId)}/`,
      signal,
    ),
};

// Live customer-specific pricing. Callers must pre-chunk to <= 50 lines/call.
export const pricingApi = {
  quote: async (
    distributorId: number,
    payload: {
      ship_to_number?: string;
      branch_number?: string;
      purpose: 'estimating' | 'quoting' | 'ordering';
      lines: QuoteLineRequest[];
    },
  ) => {
    const response = await apiClient.post(
      `/api/distributors/distributors/${distributorId}/pricing/quote/`,
      payload,
    );
    return response.data as QuoteResponse;
  },
};
